import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const PortfolioSection = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [lightboxImage, setLightboxImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([
    { id: 'all', label: 'All Projects', count: 0 },
    { id: 'websites', label: 'Websites', count: 0 },
    { id: 'mobile', label: 'Mobile Apps', count: 0 },
    { id: 'branding', label: 'Branding', count: 0 },
    { id: 'ecommerce', label: 'E-commerce', count: 0 }
  ]);

  useEffect(() => {
    const fetchPortfolioItems = async () => {
      try {
        const response = await fetch('http://localhost/phpValidation/freeuser-landing/latest_job.php?action=portfolio');
        const data = await response.json();
        
        if (data.success) {
          setPortfolioItems(data.data);
          // Update category counts
          setCategories(prevCategories => 
            prevCategories.map(cat => ({
              ...cat,
              count: data.data.filter(item => 
                cat.id === 'all' || item.category === cat.id
              ).length
            }))
          );
        }
      } catch (error) {
        console.error('Error fetching portfolio items:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPortfolioItems();
  }, []);

  const filteredItems = selectedCategory === 'all' 
    ? portfolioItems 
    : portfolioItems.filter(item => item.category === selectedCategory);

  const openLightbox = (item, imageType) => {
    setLightboxImage({ ...item, currentImage: imageType });
    setCurrentImageIndex(filteredItems.findIndex(i => i.id === item.id));
  };

  const closeLightbox = () => {
    setLightboxImage(null);
  };

  const navigateLightbox = (direction) => {
    const newIndex = direction === 'next' 
      ? (currentImageIndex + 1) % filteredItems.length
      : (currentImageIndex - 1 + filteredItems.length) % filteredItems.length;
    
    setCurrentImageIndex(newIndex);
    setLightboxImage({ ...filteredItems[newIndex], currentImage: 'after' });
  };

  if (loading) {
    return (
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading portfolio...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="portfolio" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-poppins text-gray-900 mb-6">
            Our Work
            <span className="text-blue-600"> Speaks for Itself</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            See the transformative results we've delivered for businesses across various industries.
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`flex items-center px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                selectedCategory === category.id
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {category.label}
              <span className={`ml-2 px-2 py-1 rounded-full text-xs ${
                selectedCategory === category.id
                  ? 'bg-white bg-opacity-20 text-white' 
                  : 'bg-gray-200 text-gray-600'
              }`}>
                {category.count}
              </span>
            </button>
          ))}
        </div>

        {/* Portfolio Grid */}
        {filteredItems.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredItems.map((item) => (
              <div
                key={item.id}
                className={`bg-white rounded-2xl shadow-card hover:shadow-xl transition-all duration-300 overflow-hidden group ${
                  item.featured ? 'ring-2 ring-blue-200' : ''
                }`}
              >
                {/* Before/After Images */}
                <div className="relative h-48 overflow-hidden">
                  <div className="flex h-full">
                    {/* Before Image */}
                    <div className="w-1/2 relative group/before">
                      <Image
                        src={item.beforeImage}
                        alt={`${item.title} - Before`}
                        className="w-full h-full object-cover cursor-pointer"
                        onClick={() => openLightbox(item, 'before')}
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center opacity-0 group-hover/before:opacity-100 transition-opacity duration-300">
                        <div className="text-white text-sm font-semibold bg-black bg-opacity-50 px-3 py-1 rounded-full">
                          Before
                        </div>
                      </div>
                    </div>

                    {/* Divider */}
                    <div className="w-1 bg-white relative">
                      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center">
                        <Icon name="ArrowRight" size={16} className="text-blue-600" />
                      </div>
                    </div>

                    {/* After Image */}
                    <div className="w-1/2 relative group/after">
                      <Image
                        src={item.afterImage}
                        alt={`${item.title} - After`}
                        className="w-full h-full object-cover cursor-pointer"
                        onClick={() => openLightbox(item, 'after')}
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center opacity-0 group-hover/after:opacity-100 transition-opacity duration-300">
                        <div className="text-white text-sm font-semibold bg-black bg-opacity-50 px-3 py-1 rounded-full">
                          After
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  {/* Header */}
                  <div className="mb-4">
                    <h3 className="text-xl font-bold font-poppins text-gray-900 mb-2 group-hover:text-blue-600 transition-colors duration-300">
                      {item.title}
                    </h3>
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="font-medium">{item.client}</span>
                      <span className="mx-2">•</span>
                      <span>{item.industry}</span>
                    </div>
                  </div>

                  {/* Results */}
                  <div className="mb-4">
                    <div className="text-sm font-semibold text-gray-900 mb-2">Key Results:</div>
                    <div className="space-y-1">
                      {item.results.map((result, index) => (
                        <div key={index} className="flex items-center text-sm text-gray-600">
                          <Icon name="TrendingUp" size={14} className="text-green-600 mr-2 flex-shrink-0" />
                          {result}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2">
                    {item.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="Search" size={32} className="text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No projects found</h3>
            <p className="text-gray-600">Try selecting a different category or check back later.</p>
          </div>
        )}
      </div>

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4">
          <div className="relative max-w-4xl w-full">
            {/* Close Button */}
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center text-white hover:bg-opacity-30 transition-colors z-10"
            >
              <Icon name="X" size={24} />
            </button>

            {/* Navigation Buttons */}
            <button
              onClick={() => navigateLightbox('prev')}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center text-white hover:bg-opacity-30 transition-colors"
            >
              <Icon name="ChevronLeft" size={24} />
            </button>
            <button
              onClick={() => navigateLightbox('next')}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center text-white hover:bg-opacity-30 transition-colors"
            >
              <Icon name="ChevronRight" size={24} />
            </button>

            {/* Image */}
            <div className="bg-white rounded-2xl overflow-hidden">
              <div className="aspect-video">
                <Image
                  src={lightboxImage.currentImage === 'before' ? lightboxImage.beforeImage : lightboxImage.afterImage}
                  alt={`${lightboxImage.title} - ${lightboxImage.currentImage}`}
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* Image Info */}
              <div className="p-6">
                <h3 className="text-2xl font-bold font-poppins text-gray-900 mb-2">
                  {lightboxImage.title}
                </h3>
                <p className="text-gray-600 mb-4">
                  {lightboxImage.client} • {lightboxImage.industry}
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm font-semibold text-gray-900 mb-2">Results Achieved:</div>
                    <div className="space-y-1">
                      {lightboxImage.results.map((result, index) => (
                        <div key={index} className="flex items-center text-sm text-gray-600">
                          <Icon name="Check" size={14} className="text-green-600 mr-2" />
                          {result}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-gray-900 mb-2">Technologies Used:</div>
                    <div className="flex flex-wrap gap-2">
                      {lightboxImage.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default PortfolioSection;